// Sound output on Web Audio, mixed like ET's snd_dma.c: every sound is mono
// at a point, panned with S_SpatializeOrigin's constant-energy stereo split
// and faded linearly to silence at its range (1250 units by default). Sounds
// at the listener ("local", the player's own) play centered at full volume.
// Looping sounds are kept alive frame by frame like S_AddLoopingSound.
//
// The AudioContext is created on the first user gesture (browsers start it
// suspended otherwise) and suspended while the game is paused.

import type { Vec3 } from '../shared/math';
import { SOUND_RANGE_DEFAULT } from '../shared/game/mapSounds';
import { parseWav, type PcmSound } from './wav';

export type SoundCategory = 'effects' | 'ambient';

export interface SoundVolumes {
  /** 0..1 */
  master: number;
  effects: number;
  ambient: number;
}

/** Where a sound plays: a point, a moving point (read every frame), or at the listener. */
export type SoundOrigin = Vec3 | (() => Vec3) | null;

export interface PlayOptions {
  origin?: SoundOrigin;
  /** 0..1 */
  volume?: number;
  category?: SoundCategory;
  /** Distance at which it fades out. */
  range?: number;
  /** Same volume everywhere (sound script "global"). */
  global?: boolean;
}

export interface Listener {
  origin: Vec3;
  forward: Vec3;
  /** Q3's axis[1] (the listener's left). */
  left: Vec3;
  up: Vec3;
}

/** Most sounds playing at once (ET mixes 96 channels; quieter oldest ones go first here). */
export const MAX_CHANNELS = 48;

/**
 * S_SpatializeOrigin: left and right gains (each 0..~1.41, squares summing to
 * 2 * volume^2) of a sound at `source` for the listener.
 */
export function spatialize(source: Vec3, listener: Listener, range = SOUND_RANGE_DEFAULT, volume = 1, global = false): [number, number] {
  const d: Vec3 = [source[0] - listener.origin[0], source[1] - listener.origin[1], source[2] - listener.origin[2]];
  let dist = Math.hypot(d[0], d[1], d[2]);
  if (dist > 0) for (let i = 0; i < 3; i++) d[i] /= dist;
  // default range of 1250 gives full volume up to 80 units
  dist -= range * 0.064;
  if (dist < 0 || global) dist = 0;
  else dist /= range;
  if (global) return [volume, volume];
  // VectorRotate into the listener axis: [1] is the left component
  const side = d[0] * listener.left[0] + d[1] * listener.left[1] + d[2] * listener.left[2];
  const scale = Math.max(0, 1 - dist) * volume;
  return [Math.sqrt(1 + side) * scale, Math.sqrt(Math.max(0, 1 - side)) * scale];
}

interface Channel {
  path: string;
  source: AudioBufferSourceNode;
  left: GainNode;
  right: GainNode;
  merger: ChannelMergerNode;
  opts: PlayOptions;
  started: number;
  ended: boolean;
  /** Loops: refreshed this frame. */
  touched: boolean;
}

const hasWebAudio = () => typeof AudioContext !== 'undefined';
const SMOOTH = 0.015;

export class SoundSystem {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private categoryGains: Partial<Record<SoundCategory, GainNode>> = {};
  /** path -> decoded sound (PCM until the context exists), null = could not be read. */
  private readonly sounds = new Map<string, PcmSound | AudioBuffer | null>();
  private channels: Channel[] = [];
  private readonly loops = new Map<string, Channel>();
  private listener: Listener = { origin: [0, 0, 0], forward: [1, 0, 0], left: [0, 1, 0], up: [0, 0, 1] };
  private volumes: SoundVolumes = { master: 0.8, effects: 1, ambient: 0.7 };
  private paused = false;

  /** Creates (or resumes) the audio context; call from a user gesture. */
  unlock(): void {
    if (!hasWebAudio()) return;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);
      for (const c of ['effects', 'ambient'] as SoundCategory[]) {
        const g = this.ctx.createGain();
        g.connect(this.masterGain);
        this.categoryGains[c] = g;
      }
      this.applyVolumes();
    }
    if (!this.paused && this.ctx.state === 'suspended') this.ctx.resume().catch(() => undefined);
  }

  /** True once sounds can be heard. */
  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  setVolumes(v: SoundVolumes): void {
    this.volumes = { ...v };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.ctx || !this.masterGain) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.setTargetAtTime(this.volumes.master, t, SMOOTH);
    this.categoryGains.effects?.gain.setTargetAtTime(this.volumes.effects, t, SMOOTH);
    this.categoryGains.ambient?.gain.setTargetAtTime(this.volumes.ambient, t, SMOOTH);
  }

  /** Silence while the game is paused (the context is suspended, sounds continue on resume). */
  setPaused(paused: boolean): void {
    this.paused = paused;
    if (!this.ctx) return;
    if (paused) this.ctx.suspend().catch(() => undefined);
    else this.ctx.resume().catch(() => undefined);
  }

  setListener(l: Listener): void {
    this.listener = { origin: [...l.origin], forward: [...l.forward], left: [...l.left], up: [...l.up] };
  }

  /** Adds a WAV file; returns false when it cannot be decoded. */
  add(path: string, data: Uint8Array): boolean {
    try {
      this.sounds.set(path.toLowerCase(), parseWav(data));
      return true;
    } catch (err) {
      console.warn(`sound ${path}:`, (err as Error).message);
      this.sounds.set(path.toLowerCase(), null);
      return false;
    }
  }

  has(path: string): boolean {
    const key = path.toLowerCase();
    return !!this.sounds.get(key);
  }

  /** Was the file looked at (decoded or found missing)? */
  known(path: string): boolean {
    return this.sounds.has(path.toLowerCase());
  }

  markMissing(path: string): void {
    if (!this.sounds.has(path.toLowerCase())) this.sounds.set(path.toLowerCase(), null);
  }

  private buffer(path: string): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const key = path.toLowerCase();
    const s = this.sounds.get(key);
    if (!s) return null;
    if (s instanceof AudioBuffer) return s;
    const buf = ctx.createBuffer(s.channels.length, Math.max(1, s.channels[0].length), s.sampleRate);
    s.channels.forEach((data, c) => buf.copyToChannel(data as Float32Array<ArrayBuffer>, c));
    this.sounds.set(key, buf);
    return buf;
  }

  private start(path: string, opts: PlayOptions, loop: boolean): Channel | null {
    const ctx = this.ctx;
    const cat = this.categoryGains[opts.category ?? 'effects'];
    if (!ctx || !cat || this.paused) return null;
    const buf = this.buffer(path);
    if (!buf) return null;
    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.loop = loop;
    const left = ctx.createGain();
    const right = ctx.createGain();
    const merger = ctx.createChannelMerger(2);
    source.connect(left);
    source.connect(right);
    left.connect(merger, 0, 0);
    right.connect(merger, 0, 1);
    merger.connect(cat);
    const ch: Channel = { path, source, left, right, merger, opts, started: ctx.currentTime, ended: false, touched: true };
    this.pan(ch, true);
    source.onended = () => {
      ch.ended = true;
      merger.disconnect();
    };
    source.start();
    return ch;
  }

  private pan(ch: Channel, immediate = false): void {
    const o = typeof ch.opts.origin === 'function' ? ch.opts.origin() : ch.opts.origin;
    const vol = ch.opts.volume ?? 1;
    // local sounds: centered, no attenuation
    const [l, r] = o ? spatialize(o, this.listener, ch.opts.range, vol, ch.opts.global) : [vol, vol];
    if (immediate || !this.ctx) {
      ch.left.gain.value = l;
      ch.right.gain.value = r;
    } else {
      ch.left.gain.setTargetAtTime(l, this.ctx.currentTime, SMOOTH);
      ch.right.gain.setTargetAtTime(r, this.ctx.currentTime, SMOOTH);
    }
  }

  /** Plays a sound once; returns false when it is not available (or audio is off). */
  play(path: string, opts: PlayOptions = {}): boolean {
    const ch = this.start(path, opts, false);
    if (!ch) return false;
    this.channels = this.channels.filter((c) => !c.ended);
    if (this.channels.length >= MAX_CHANNELS) {
      const oldest = this.channels.shift()!;
      oldest.source.stop();
    }
    this.channels.push(ch);
    return true;
  }

  /** Keeps a looping sound playing this frame (S_AddLoopingSound); loops not kept are stopped by update(). */
  loop(key: string, path: string, opts: PlayOptions = {}): void {
    const ch = this.loops.get(key);
    if (ch && ch.path === path) {
      ch.opts = opts;
      ch.touched = true;
      return;
    }
    if (ch) this.stopChannel(ch);
    this.loops.delete(key);
    const started = this.start(path, opts, true);
    if (started) this.loops.set(key, started);
  }

  private stopChannel(ch: Channel): void {
    try {
      ch.source.stop();
    } catch {
      // never started
    }
    ch.merger.disconnect();
  }

  /** Once per frame after setListener: pans moving sounds and drops loops nobody kept. */
  update(): void {
    for (const ch of this.channels) if (!ch.ended && typeof ch.opts.origin === 'function') this.pan(ch);
    for (const [key, ch] of this.loops) {
      if (!ch.touched) {
        this.stopChannel(ch);
        this.loops.delete(key);
        continue;
      }
      ch.touched = false;
      this.pan(ch);
    }
    // the listener moves: pan the fixed sounds that are still playing too
    for (const ch of this.channels) if (!ch.ended && Array.isArray(ch.opts.origin)) this.pan(ch);
  }

  /** Stops everything (map change). */
  stopAll(): void {
    for (const ch of this.channels) if (!ch.ended) this.stopChannel(ch);
    for (const ch of this.loops.values()) this.stopChannel(ch);
    this.channels = [];
    this.loops.clear();
  }

  /** Forgets the sounds of a map (keeps the ones in `keep`). */
  forget(keep: (path: string) => boolean): void {
    for (const key of [...this.sounds.keys()]) if (!keep(key)) this.sounds.delete(key);
  }
}
